import Constants from 'expo-constants';
import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useAuthContext } from '@fhs/auth/src/hooks';
import { IUserAccountsQuery, useUserAccountsQuery } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { ISOs } from '@fhs-legacy/frontend/src/utils/form/constants';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import logger from '@fhs-legacy/frontend/src/utils/logger';

import { usePaymentMethodStore } from '../../../../payment';

import { useApplePayAvailability, useGooglePayAvailability } from './digital-wallet-availability';

function hasUserAccountsInStore(data: IUserAccountsQuery): boolean {
  return Object.prototype.hasOwnProperty.call(data, 'userAccounts');
}

const emptyUserAccountsQuery = (): IUserAccountsQuery => ({
  userAccounts: {
    accounts: [],
    count: 0,
  },
});

export default function useAvailablePaymentMethods() {
  const { formatMessage } = useIntl();
  const { user, isAuthenticated, loading: authLoading } = useAuthContext();
  const setUserAccountsData = usePaymentMethodStore(s => s.setUserAccountsData);
  const setUserAccountsLoadError = usePaymentMethodStore(s => s.setUserAccountsLoadError);
  const setShowAddPaymentMethod = usePaymentMethodStore(s => s.setShowAddPaymentMethod);
  const setSelectedPaymentMethod = usePaymentMethodStore(s => s.setSelectedPaymentMethod);
  const currentUserAccountsData = usePaymentMethodStore(s => s.userAccountsData);

  const canUseGiftCards = useFlag('enable-fhs-gift-cards');
  const canUseApplePay = useApplePayAvailability();
  const canUseGooglePay = useGooglePayAvailability();

  const defaultAccountIdentifier = user?.details?.defaultAccountIdentifier;

  const feAlpha3CountryCode = Constants.expoConfig?.extra?.eas.buildRegion === 'US' ? 'USA' : 'CAN';

  const hasStoredUserAccounts = hasUserAccountsInStore(currentUserAccountsData);
  const skipUserAccountsQuery = hasStoredUserAccounts || !isAuthenticated;

  const {
    data: userAccountsData,
    loading: userAccountsLoading,
    error: userAccountsError,
  } = useUserAccountsQuery({
    variables: { feCountryCode: feAlpha3CountryCode as ISOs },
    fetchPolicy: 'network-only',
    skip: skipUserAccountsQuery,
  });

  const didHandleFetchFailureRef = useRef(false);

  const paymentLoadingErrorMessage = formatMessage({ id: 'paymentLoadingError' });

  const applyPaymentMethodsFallback = useCallback(() => {
    if (hasUserAccountsInStore(usePaymentMethodStore.getState().userAccountsData)) {
      return;
    }
    setUserAccountsData(emptyUserAccountsQuery(), defaultAccountIdentifier, {
      canUseApplePay,
      canUseGooglePay,
    });
  }, [setUserAccountsData, defaultAccountIdentifier, canUseApplePay, canUseGooglePay]);

  const handlePaymentMethodsFetchFailure = useCallback(
    (error: unknown) => {
      if (didHandleFetchFailureRef.current) {
        return;
      }
      didHandleFetchFailureRef.current = true;

      logger.error({
        error,
        message: 'Error getting payment methods',
      });

      applyPaymentMethodsFallback();
      // Empty accounts fallback may pick Google/Apple Pay; on error always show add-new-card UI.
      setSelectedPaymentMethod(null);
      setShowAddPaymentMethod(true);
      setUserAccountsLoadError(paymentLoadingErrorMessage);
    },
    [
      setUserAccountsLoadError,
      paymentLoadingErrorMessage,
      applyPaymentMethodsFallback,
      setSelectedPaymentMethod,
      setShowAddPaymentMethod,
    ]
  );

  useEffect(() => {
    if (!userAccountsError) {
      return;
    }
    handlePaymentMethodsFetchFailure(userAccountsError);
  }, [userAccountsError, handlePaymentMethodsFetchFailure]);

  useEffect(() => {
    if (!userAccountsData) {
      return;
    }
    if (isAuthenticated && !user) {
      return;
    }

    if (userAccountsData !== currentUserAccountsData) {
      setUserAccountsData(userAccountsData, defaultAccountIdentifier, {
        canUseApplePay,
        canUseGooglePay,
      });
    }
  }, [
    userAccountsData,
    currentUserAccountsData,
    setUserAccountsData,
    defaultAccountIdentifier,
    canUseApplePay,
    canUseGooglePay,
    isAuthenticated,
    user,
  ]);

  useEffect(() => {
    if (
      skipUserAccountsQuery ||
      userAccountsLoading ||
      userAccountsError ||
      hasStoredUserAccounts ||
      !isAuthenticated ||
      authLoading ||
      user ||
      !userAccountsData
    ) {
      return;
    }

    handlePaymentMethodsFetchFailure(
      new Error('User profile unavailable after payment methods response')
    );
  }, [
    skipUserAccountsQuery,
    userAccountsLoading,
    userAccountsError,
    hasStoredUserAccounts,
    isAuthenticated,
    authLoading,
    user,
    userAccountsData,
    handlePaymentMethodsFetchFailure,
  ]);

  const isUserAccountsPending = isAuthenticated && !hasStoredUserAccounts && !userAccountsError;

  return {
    canUseGiftCards,
    canUseApplePay,
    canUseGooglePay,
    userAccountsLoading,
    isUserAccountsPending,
  };
}
